import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "sonner";

import { OutgoingIntakesAPI } from "@/api/outgoingIntakesApi";
import type { WelcomeVideoUploadMeta } from "@/api/types/outgoingIntake";
import { intakeEstimate } from "@/features/foreignNationalOutgoingIntakes/intakeEstimate";

import { lawyerSelectors } from "../../../../redux/lawyer/selectors";
import { useRequestsEnv } from "../../context/RequestsEnvContext";
import { useRequestsIde } from "../../context/RequestsIdeContext";
import { useRequestsData } from "../../data/RequestsDataContext";
import type { IntakeDraft } from "../useIntakeDraft";
import {
  intakeVideoKey,
  startVideoUpload,
  useVideoUpload,
  type VideoUpload
} from "./uploadStore";
import type { TalkingPointsInput } from "./welcomeVideo";

export type IntakeWelcomeVideo = {
  url: string | null;
  /** The library entry it plays from, when it's a saved or firm video. */
  videoId: string | null;
  upload: VideoUpload | null;
  uploadFile: (file: File, meta?: WelcomeVideoUploadMeta) => void;
  /** Use a Previous Video. */
  pickLibraryVideo: (videoId: string) => Promise<void>;
  remove: () => Promise<void>;
  recording: boolean;
  setRecording: (open: boolean) => void;
  library: boolean;
  setLibrary: (open: boolean) => void;
  talkingPoints: TalkingPointsInput;
  /** Casey's talking points; empty when it couldn't. */
  askCasey: () => Promise<string[]>;
};

/** "October 29". */
const spoken = (date: Date) =>
  date.toLocaleDateString("en-US", { month: "long", day: "numeric" });

/** The draft's welcome video: upload (kept going across pages), remove, record. */
export const useIntakeWelcomeVideo = (
  draft: IntakeDraft,
  clientFirstName: string
): IntakeWelcomeVideo => {
  const { clientId } = useRequestsIde();
  const data = useRequestsData();
  const env = useRequestsEnv();
  const attorneyName = useSelector(lawyerSelectors.selectFullName);
  const [recording, setRecording] = useState(false);
  const [library, setLibrary] = useState(false);
  const { intake } = draft;
  const intakeId = intake?.outgoing_intake_id ?? null;
  const upload = useVideoUpload(intakeId ? intakeVideoKey(intakeId) : null);

  const talkingPoints = useMemo(() => {
    const due = new Date();
    due.setDate(due.getDate() + draft.settings.expires_in_days);
    return {
      clientName: clientFirstName,
      attorneyName: attorneyName ?? null,
      firmName: env.lawFirmName ?? intake?.practice_name ?? null,
      title: draft.title,
      estimate: intakeEstimate(draft.schema),
      due
    };
  }, [
    attorneyName,
    clientFirstName,
    draft.schema,
    draft.title,
    draft.settings.expires_in_days,
    env.lawFirmName,
    intake?.practice_name
  ]);

  const uploadFile = (file: File, meta: WelcomeVideoUploadMeta = {}) => {
    if (!intakeId) return;
    startVideoUpload({
      key: intakeVideoKey(intakeId),
      upload: (onProgress, signal) =>
        OutgoingIntakesAPI.uploadWelcomeVideo(
          clientId,
          intakeId,
          file,
          file.name,
          onProgress,
          signal,
          meta
        ),
      onDone: data.applyIntake,
      doneMessage: meta.saveToMyVideos
        ? "Welcome video added and saved to My Videos"
        : undefined
    });
  };

  const pickLibraryVideo = async (videoId: string) => {
    if (!intakeId) return;
    data.applyIntake(
      await OutgoingIntakesAPI.attachLibraryWelcomeVideo(
        clientId,
        intakeId,
        videoId
      )
    );
  };

  const askCasey = async () => {
    if (!intakeId) return [];
    return OutgoingIntakesAPI.draftTalkingPoints(clientId, intakeId, {
      attorney_name: attorneyName ?? null,
      firm_name: talkingPoints.firmName,
      due: talkingPoints.due ? spoken(talkingPoints.due) : null
    });
  };

  const remove = async () => {
    if (!intakeId) return;
    try {
      data.applyIntake(
        await OutgoingIntakesAPI.deleteWelcomeVideo(clientId, intakeId)
      );
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Couldn't remove the video");
    }
  };

  return {
    url: intake?.welcome_video_url ?? null,
    videoId: intake?.welcome_video_id ?? null,
    upload,
    uploadFile,
    pickLibraryVideo,
    remove,
    recording,
    setRecording,
    library,
    setLibrary,
    talkingPoints,
    askCasey
  };
};
